import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { clinicService } from "../services/api";

// ─── CustomAlert ──────────────────────────────────────────────────────────────
function CustomAlert({ alert, onClose }) {
    if (!alert) return null;

    const cfg = {
        success: {
            iconBg: "bg-emerald-100",
            iconColor: "text-emerald-500",
            btnGradient: "bg-gradient-to-r from-emerald-400 to-emerald-500",
            btnHover: "hover:from-emerald-500 hover:to-emerald-600",
        },
        error: {
            iconBg: "bg-red-100",
            iconColor: "text-red-500",
            btnGradient: "bg-gradient-to-r from-red-400 to-red-500",
            btnHover: "hover:from-red-500 hover:to-red-600",
        },
        warning: {
            iconBg: "bg-amber-100",
            iconColor: "text-amber-500",
            btnGradient: "bg-gradient-to-r from-amber-400 to-yellow-400",
            btnHover: "hover:from-amber-500 hover:to-yellow-500",
        },
        info: {
            iconBg: "bg-cyan-100",
            iconColor: "text-cyan-500",
            btnGradient: "bg-gradient-to-r from-cyan-400 to-cyan-500",
            btnHover: "hover:from-cyan-500 hover:to-cyan-600",
        },
    };

    const icons = {
        success: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <polyline points="20 6 9 17 4 12" />
            </svg>
        ),
        error: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
        ),
        warning: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
        ),
        info: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
        ),
    };

    const c = cfg[alert.type] || cfg.info;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/10 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, y: 16, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.98 }}
                    transition={{ duration: 0.22, ease: [0.22, 0.68, 0, 1.2] }}
                    className="bg-white rounded-2xl w-full max-w-xs overflow-hidden shadow-xl ring-1 ring-black/5 p-5"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Icon + Text Row */}
                    <div className="flex items-start gap-3 mb-5">
                        <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.iconBg} ${c.iconColor}`}
                        >
                            {icons[alert.type]}
                        </div>
                        <div className="pt-0.5">
                            <p className="text-sm font-bold text-gray-800 mb-0.5">
                                {alert.title}
                            </p>
                            <p className="text-xs text-gray-400 leading-relaxed">
                                {alert.message}
                            </p>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="flex-1 py-2.5 rounded-xl text-xs font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all border border-gray-100"
                        >
                            Tutup
                        </button>
                        <button
                            onClick={onClose}
                            className={`flex-[2] py-2.5 rounded-xl text-xs font-semibold text-white transition-all shadow-sm ${c.btnGradient} ${c.btnHover}`}
                        >
                            {alert.btnText || "Mengerti"}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// ─── Loading Overlay ──────────────────────────────────────────────────────────
function LoadingOverlay() {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/10 backdrop-blur-sm"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.22, ease: [0.22, 0.68, 0, 1.2] }}
                className="bg-white rounded-2xl px-8 py-7 flex flex-col items-center gap-4 shadow-2xl ring-1 ring-black/5 min-w-[220px]"
            >
                {/* Spinner */}
                <svg
                    className="-rotate-90"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                >
                    <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="#e2edef"
                        strokeWidth="3"
                    />
                    <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray="50 101"
                        stroke="url(#rr-lg)"
                        style={{
                            animation: "rr-spin-dash 0.9s linear infinite",
                        }}
                    />
                    <defs>
                        <linearGradient
                            id="rr-lg"
                            x1="0%"
                            y1="0%"
                            x2="100%"
                            y2="0%"
                        >
                            <stop offset="0%" stopColor="#22d3ee" />
                            <stop offset="100%" stopColor="#0891b2" />
                        </linearGradient>
                    </defs>
                </svg>

                <div className="flex flex-col items-center gap-0.5 text-center">
                    <p
                        className="text-[0.82rem] font-semibold text-[#0d1a1c]"
                        style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                    >
                        Memproses...
                    </p>
                    <p className="text-[0.72rem] text-[#8fa3a8]">
                        Harap tunggu sebentar
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );
}

// ─── Register ─────────────────────────────────────────────────────────────────
function ClinicRegister() {
    const navigate = useNavigate();
    const { slug } = useParams();

    const [clinic, setClinic] = useState(null);
    const [clinicState, setClinicState] = useState("loading");

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
    });

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [alertData, setAlertData] = useState(null);

    // Fetch clinic branding on mount
    useEffect(() => {
        if (!slug) { setClinicState("notfound"); return; }
        clinicService.getBySlug(slug)
            .then((res) => {
                if (res.data?.success) {
                    setClinic(res.data.data);
                    setClinicState("found");
                } else {
                    setClinicState("notfound");
                }
            })
            .catch(() => setClinicState("notfound"));
    }, [slug]);

    const showAlert = (type, title, message, btnText) =>
        setAlertData({ type, title, message, btnText });
    const closeAlert = () => setAlertData(null);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrors({});

        try {
            // POST to the clinic-specific register endpoint, not standard /register
            const response = await api.post(`/${slug}/register`, formData);

            console.log("Success:", response.data);
            showAlert(
                "success",
                "Registrasi Berhasil!",
                "Akun Anda berhasil dibuat. Silahkan login untuk melanjutkan.",
                "Masuk Sekarang",
            );

            // Navigate after alert closed — attach navigate to closeAlert
            setAlertData({
                type: "success",
                title: "Registrasi Berhasil!",
                message:
                    "Akun Anda berhasil dibuat. Silahkan login untuk melanjutkan.",
                btnText: "Masuk Sekarang",
                onConfirm: () => navigate(`/${slug}/login`),
            });
        } catch (error) {
            if (error.response && error.response.data.errors) {
                setErrors(error.response.data.errors);
            } else {
                console.error("Error:", error);
                showAlert(
                    "error",
                    "Terjadi Kesalahan",
                    error.response?.data?.message || error.message,
                    "Coba Lagi",
                );
            }
        } finally {
            setIsLoading(false);
        }
    };

    // closeAlert with optional onConfirm callback
    const handleCloseAlert = () => {
        const cb = alertData?.onConfirm;
        setAlertData(null);
        if (cb) cb();
    };

    if (clinicState === "loading") {
        return <LoadingOverlay />;
    }

    if (clinicState === "notfound") {
        return (
            <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50 text-slate-600 text-sm font-['DM_Sans',sans-serif]">
                Klinik tidak ditemukan. Pastikan link yang Anda buka benar.
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full flex overflow-hidden font-['DM_Sans',sans-serif]">
            <CustomAlert alert={alertData} onClose={handleCloseAlert} />
            <AnimatePresence>{isLoading && <LoadingOverlay />}</AnimatePresence>

            {/* ── LEFT PANEL ── */}
            <div
                className="hidden lg:flex w-1/2 shrink-0 relative overflow-hidden items-center justify-center"
                style={{
                    background:
                        "linear-gradient(150deg, #0e7490 0%, #0891b2 40%, #22d3ee 80%, #06b6d4 100%)",
                }}
            >
                {/* Orb decorations */}
                <div
                    className="absolute rounded-full pointer-events-none"
                    style={{
                        top: "-90px",
                        right: "-70px",
                        width: "350px",
                        height: "350px",
                        background: "rgba(255,255,255,0.07)",
                        animation: "rl-orb 7s ease-in-out infinite",
                    }}
                />
                <div
                    className="absolute rounded-full pointer-events-none"
                    style={{
                        bottom: "-70px",
                        left: "-50px",
                        width: "260px",
                        height: "260px",
                        background: "rgba(255,255,255,0.05)",
                        animation: "rl-orb 9s ease-in-out infinite reverse",
                    }}
                />
                <div
                    className="absolute rounded-full"
                    style={{
                        top: "50px",
                        left: "30px",
                        width: "130px",
                        height: "130px",
                        border: "1px solid rgba(255,255,255,0.13)",
                        background: "rgba(255,255,255,0.06)",
                        animation: "rl-orb 6s ease-in-out infinite",
                    }}
                />
                <div
                    className="absolute rounded-full"
                    style={{
                        top: "50%",
                        right: "-25px",
                        transform: "translateY(-50%)",
                        width: "200px",
                        height: "200px",
                        border: "1px solid rgba(255,255,255,0.09)",
                        background: "rgba(255,255,255,0.04)",
                        animation: "rl-orb 8s ease-in-out infinite reverse",
                    }}
                />

                {/* Left Content */}
                <div
                    className="relative z-10 p-10 flex flex-col items-center text-center"
                    style={{
                        animation:
                            "rl-slide-left 0.7s cubic-bezier(.22,.68,0,1.2) both",
                    }}
                >
                    {/* Badge */}
                    <div
                        className="inline-flex items-center gap-[7px] rounded-full px-4 py-1.5 mb-5 backdrop-blur-sm"
                        style={{
                            background: "rgba(255,255,255,0.15)",
                            border: "1px solid rgba(255,255,255,0.25)",
                        }}
                    >
                        <div
                            className="w-1.5 h-1.5 rounded-full bg-white"
                            style={{
                                animation: "rl-pulse 2s ease-in-out infinite",
                            }}
                        />
                        <span className="text-[0.68rem] font-semibold tracking-[0.1em] uppercase text-white/90">
                            Bergabung Sekarang
                        </span>
                    </div>

                    <h1
                        className="text-[2rem] font-bold text-white leading-tight mb-3 tracking-tight"
                        style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                    >
                        Mulai Perjalanan
                        <br />
                        Bersama CareLine
                    </h1>
                    <p className="text-[0.85rem] text-white/70 leading-relaxed max-w-[280px] font-light mb-6">
                        Daftarkan diri dan nikmati kemudahan mengelola layanan
                        kesehatan dalam satu platform terintegrasi.
                    </p>

                    {/* Steps */}
                    <div className="flex flex-col gap-0 w-full max-w-[280px] text-left">
                        {[
                            {
                                num: "1",
                                delay: "0.2s",
                                title: "Buat Akun",
                                desc: "Isi data diri dan buat kata sandi",
                            },
                            {
                                num: "2",
                                delay: "0.33s",
                                title: "Verifikasi Email",
                                desc: "Konfirmasi akun melalui email Anda",
                            },
                            {
                                num: "3",
                                delay: "0.46s",
                                title: "Mulai Gunakan",
                                desc: "Akses semua fitur CareLine",
                            },
                        ].map((step, i, arr) => (
                            <div
                                key={i}
                                className="flex items-start gap-3 relative"
                                style={{
                                    paddingBottom:
                                        i < arr.length - 1 ? "1.2rem" : "0",
                                    animation: `rl-fade-up 0.5s ease ${step.delay} both`,
                                }}
                            >
                                {i < arr.length - 1 && (
                                    <div
                                        className="absolute left-[13px] top-7 w-px"
                                        style={{
                                            height: "calc(100% - 14px)",
                                            background: "rgba(255,255,255,0.2)",
                                        }}
                                    />
                                )}
                                <div
                                    className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[0.72rem] font-bold text-white"
                                    style={{
                                        background: "rgba(255,255,255,0.2)",
                                        border: "1.5px solid rgba(255,255,255,0.35)",
                                    }}
                                >
                                    {step.num}
                                </div>
                                <div>
                                    <strong className="block text-[0.82rem] font-semibold text-white mb-0.5">
                                        {step.title}
                                    </strong>
                                    <span className="text-[0.72rem] text-white/[0.58] font-light">
                                        {step.desc}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── RIGHT PANEL ── */}
            <div className="flex-1 h-screen overflow-hidden bg-[#f4f8f9] flex items-center justify-center p-5 relative">
                <div
                    className="absolute top-0 right-0 w-40 h-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(circle at top right, rgba(8,145,178,0.07) 0%, transparent 70%)",
                    }}
                />

                <div
                    className="bg-white rounded-2xl px-8 py-7 w-full max-w-[400px]"
                    style={{
                        boxShadow:
                            "0 1px 2px rgba(0,0,0,0.04), 0 6px 22px rgba(8,145,178,0.1), 0 0 0 1px rgba(0,0,0,0.04)",
                        animation:
                            "rr-slide-right 0.65s cubic-bezier(.22,.68,0,1.2) both",
                    }}
                >
                    {/* Logo Area */}
                    <div className="flex flex-col items-center mb-4">
                        <div style={{
                            width: "24px", height: "24px", borderRadius: "50%",
                            background: "linear-gradient(135deg, #06B6D4, #0891B2)",
                            marginBottom: "10px"
                        }} />
                        <h2
                            className="text-[1.28rem] font-bold text-[#0d1a1c] mb-0.5 text-center"
                            style={{
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                            }}
                        >
                            Buat Akun di {clinic?.nama_klinik || "Klinik"}
                        </h2>
                        <p className="text-[0.78rem] text-[#8fa3a8] text-center">
                            Lengkapi data di bawah untuk mendaftar
                        </p>
                    </div>

                    {/* Divider */}
                    <div
                        className="h-px my-3"
                        style={{
                            background:
                                "linear-gradient(to right, transparent, rgba(8,145,178,0.18), transparent)",
                        }}
                    />

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* Name */}
                        <div className="mb-3">
                            <label className="block text-[0.7rem] font-semibold tracking-[0.07em] uppercase text-[#4a6568] mb-1.5">
                                Nama Lengkap
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Masukkan Nama anda"
                                className={`w-full px-3.5 py-2.5 rounded-[10px] text-[0.85rem] text-[#0d1a1c] outline-none transition-all duration-200 placeholder:text-[#b0c4c8] placeholder:font-light ${errors.name ? "bg-[#fff8f8]" : "bg-[#f8fbfc] focus:bg-white"}`}
                                style={{
                                    border: `1.5px solid ${errors.name ? "#f87171" : "#e2edef"}`,
                                    fontFamily: "'DM Sans', sans-serif",
                                }}
                                onFocus={(e) => {
                                    if (!errors.name) {
                                        e.target.style.borderColor = "#0891b2";
                                        e.target.style.boxShadow =
                                            "0 0 0 3px rgba(8,145,178,0.11)";
                                    }
                                }}
                                onBlur={(e) => {
                                    if (!errors.name) {
                                        e.target.style.borderColor = "#e2edef";
                                        e.target.style.boxShadow = "none";
                                    }
                                }}
                            />
                            {errors.name && (
                                <p className="text-[0.72rem] text-[#e05252] mt-1 flex items-center gap-1">
                                    <svg
                                        width="11"
                                        height="11"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line
                                            x1="12"
                                            y1="16"
                                            x2="12.01"
                                            y2="16"
                                        />
                                    </svg>
                                    {errors.name[0]}
                                </p>
                            )}
                        </div>

                        {/* Email */}
                        <div className="mb-3">
                            <label className="block text-[0.7rem] font-semibold tracking-[0.07em] uppercase text-[#4a6568] mb-1.5">
                                Email
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Masukkan Email anda"
                                className={`w-full px-3.5 py-2.5 rounded-[10px] text-[0.85rem] text-[#0d1a1c] outline-none transition-all duration-200 placeholder:text-[#b0c4c8] placeholder:font-light ${errors.email ? "bg-[#fff8f8]" : "bg-[#f8fbfc] focus:bg-white"}`}
                                style={{
                                    border: `1.5px solid ${errors.email ? "#f87171" : "#e2edef"}`,
                                    fontFamily: "'DM Sans', sans-serif",
                                }}
                                onFocus={(e) => {
                                    if (!errors.email) {
                                        e.target.style.borderColor = "#0891b2";
                                        e.target.style.boxShadow =
                                            "0 0 0 3px rgba(8,145,178,0.11)";
                                    }
                                }}
                                onBlur={(e) => {
                                    if (!errors.email) {
                                        e.target.style.borderColor = "#e2edef";
                                        e.target.style.boxShadow = "none";
                                    }
                                }}
                            />
                            {errors.email && (
                                <p className="text-[0.72rem] text-[#e05252] mt-1 flex items-center gap-1">
                                    <svg
                                        width="11"
                                        height="11"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line
                                            x1="12"
                                            y1="16"
                                            x2="12.01"
                                            y2="16"
                                        />
                                    </svg>
                                    {errors.email[0]}
                                </p>
                            )}
                        </div>

                        {/* Password */}
                        <div className="mb-3">
                            <label className="block text-[0.7rem] font-semibold tracking-[0.07em] uppercase text-[#4a6568] mb-1.5">
                                Kata Sandi
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Masukkan kata sandi anda"
                                    className={`w-full px-3.5 py-2.5 pr-10 rounded-[10px] text-[0.85rem] text-[#0d1a1c] outline-none transition-all duration-200 placeholder:text-[#b0c4c8] placeholder:font-light ${errors.password ? "bg-[#fff8f8]" : "bg-[#f8fbfc] focus:bg-white"}`}
                                    style={{
                                        border: `1.5px solid ${errors.password ? "#f87171" : "#e2edef"}`,
                                        fontFamily: "'DM Sans', sans-serif",
                                    }}
                                    onFocus={(e) => {
                                        if (!errors.password) {
                                            e.target.style.borderColor =
                                                "#0891b2";
                                            e.target.style.boxShadow =
                                                "0 0 0 3px rgba(8,145,178,0.11)";
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (!errors.password) {
                                            e.target.style.borderColor =
                                                "#e2edef";
                                            e.target.style.boxShadow = "none";
                                        }
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowPassword(!showPassword)
                                    }
                                    className="absolute right-[11px] top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-[#8fa3a8] p-1 flex items-center transition-colors duration-200 hover:text-[#0891b2]"
                                >
                                    {showPassword ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                            <line
                                                x1="1"
                                                y1="1"
                                                x2="23"
                                                y2="23"
                                            ></line>
                                        </svg>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {errors.password ? (
                                <p className="text-[0.72rem] text-[#e05252] mt-1 flex items-center gap-1">
                                    <svg
                                        width="11"
                                        height="11"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line
                                            x1="12"
                                            y1="16"
                                            x2="12.01"
                                            y2="16"
                                        />
                                    </svg>
                                    {errors.password[0]}
                                </p>
                            ) : (
                                <div className="flex items-center gap-1.5 mt-1 text-[0.71rem] text-[#8fa3a8]">
                                    <svg
                                        width="10"
                                        height="10"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line
                                            x1="12"
                                            y1="16"
                                            x2="12.01"
                                            y2="16"
                                        />
                                    </svg>
                                    Minimal 8 karakter
                                </div>
                            )}
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full py-[11px] mt-3.5 rounded-[10px] border-none text-white text-[0.86rem] font-semibold tracking-[0.04em] cursor-pointer transition-all duration-200 disabled:opacity-[0.58] disabled:cursor-not-allowed hover:enabled:-translate-y-px active:enabled:translate-y-0"
                            style={{
                                background:
                                    "linear-gradient(135deg, #06B6D4 0%, #0891b2 100%)",
                                boxShadow: "0 4px 14px rgba(8,145,178,0.32)",
                                fontFamily: "'DM Sans', sans-serif",
                            }}
                            onMouseEnter={(e) => {
                                if (!isLoading)
                                    e.currentTarget.style.boxShadow =
                                        "0 6px 18px rgba(8,145,178,0.42)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow =
                                    "0 4px 14px rgba(8,145,178,0.32)";
                            }}
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <span
                                        className="inline-block w-[13px] h-[13px] rounded-full border-2 border-white/30 border-t-white"
                                        style={{
                                            animation:
                                                "rr-spin 0.65s linear infinite",
                                        }}
                                    />
                                    Memproses...
                                </span>
                            ) : (
                                "Daftar Sekarang"
                            )}
                        </button>
                    </form>

                    <div className="mt-3.5 text-center text-[0.78rem] text-[#8fa3a8]">
                        Sudah Punya Akun?{" "}
                        <Link
                            to={`/${slug}/login`}
                            className="text-[#0891b2] font-semibold no-underline transition-colors duration-200 hover:text-[#0e7490]"
                        >
                            Masuk di Sini
                        </Link>
                    </div>
                </div>
            </div>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
                @keyframes rl-orb { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-16px); } }
                @keyframes rl-slide-left { from { opacity: 0; transform: translateX(-28px); } to { opacity: 1; transform: translateX(0); } }
                @keyframes rr-slide-right { from { opacity: 0; transform: translateX(26px); } to { opacity: 1; transform: translateX(0); } }
                @keyframes rl-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.72); } }
                @keyframes rl-fade-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes rr-pop { from { opacity: 0; transform: scale(0.82); } to { opacity: 1; transform: scale(1); } }
                @keyframes rr-spin { to { transform: rotate(360deg); } }
                @keyframes rr-spin-dash { 0% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -101; } }
            `}</style>
        </div>
    );
}

export default ClinicRegister;